import React, { useState, useEffect } from 'react';
import { FileCheck, X, Printer, FileDown, Send } from 'lucide-react';
import { AosrAct, Certificate } from '../../types';
import { generateAosrHtml } from '../../utils/exportAosr';
import { exportAosrToDocx } from '../../utils/exportDocx';

interface AosrGeneratorModalProps {
  initialWorkItem?: any;
  item?: any;
  onClose: () => void;
  onSuccess: () => void;
}

export const AosrGeneratorModal: React.FC<AosrGeneratorModalProps> = ({
  initialWorkItem,
  item,
  onClose,
  onSuccess
}) => {
  const targetItem = initialWorkItem || item;
  const [actNumber, setActNumber] = useState(`АОСР-ЭОМ-${Math.floor(100 + Math.random() * 900)}/26`);
  const [title, setTitle] = useState(
    targetItem?.work_type_name
      ? `${targetItem.work_type_name} в ${targetItem.zone_name || 'стояке СТ-1'} на отм. +${(targetItem.floor_number || 4) * 3}.000`
      : 'Прокладка силового кабеля ВВГнг(А)-LS 3х2.5 в стояке СТ-1'
  );
  const [startDate, setStartDate] = useState(targetItem?.report_date || '2026-08-01');
  const [endDate, setEndDate] = useState(targetItem?.report_date || '2026-08-05');
  const [projectCode, setProjectCode] = useState('SZ-2026-ЭОМ1');
  const [projectDrawings, setProjectDrawings] = useState('Листы ЭОМ 12-16 (План кабельных трасс)');
  const [createdByPto, setCreatedByPto] = useState('Инженер ПТО Васильев К.М.');
  
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [selectedCertIds, setSelectedCertIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch('/api/v1/pto/certificates')
      .then(r => r.json())
      .then(d => {
        if (d.success) {
          setCertificates(d.data);
          // Select active certificates by default
          setSelectedCertIds(d.data.slice(0, 2).map((c: Certificate) => c.id));
        }
      });
  }, []);

  const handlePrintPreview = () => {
    const mockAct: AosrAct = {
      id: 'preview',
      actNumber,
      projectId: targetItem?.project_id || 'proj-sz-2026',
      blockId: targetItem?.block_id || 'block-a',
      floorId: targetItem?.floor_id || 'blk-a-fl-4',
      zoneId: targetItem?.zone_id || 'blk-a-zone-fl4-r1',
      title,
      workTypeId: targetItem?.work_type_id || 'wt-cab-3x25',
      startDate,
      endDate,
      projectCode,
      projectDrawings,
      status: 'draft',
      createdByPto,
      createdAt: new Date().toISOString(),
      block_name: targetItem?.block_name || 'Блок А',
      floor_name: targetItem?.floor_name || '4 этаж',
      zone_name: targetItem?.zone_name || 'Стояк СТ-1'
    };

    const selectedCerts = certificates.filter(c => selectedCertIds.includes(c.id));
    const html = generateAosrHtml(mockAct, { name: 'ЖК «Северная Звезда»' }, selectedCerts);

    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
    }
  };

  const handleExportDocx = () => {
    const mockAct: AosrAct = {
      id: 'export-docx',
      actNumber,
      projectId: targetItem?.project_id || 'proj-sz-2026',
      blockId: targetItem?.block_id || 'block-a',
      floorId: targetItem?.floor_id || 'blk-a-fl-4',
      zoneId: targetItem?.zone_id || 'blk-a-zone-fl4-r1',
      title,
      workTypeId: targetItem?.work_type_id || 'wt-cab-3x25',
      startDate,
      endDate,
      projectCode,
      projectDrawings,
      status: 'draft',
      createdByPto,
      createdAt: new Date().toISOString(),
      block_name: targetItem?.block_name || 'Блок А',
      floor_name: targetItem?.floor_name || '4 этаж',
      zone_name: targetItem?.zone_name || 'Стояк СТ-1'
    };

    const selectedCerts = certificates.filter(c => selectedCertIds.includes(c.id));
    exportAosrToDocx(mockAct, { name: 'ЖК «Северная Звезда»' }, selectedCerts);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch('/api/v1/pto/aosr', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          actNumber,
          title,
          reportWorkItemId: targetItem?.report_work_item_id || null,
          startDate,
          endDate,
          projectCode,
          projectDrawings,
          certificateIds: selectedCertIds,
          createdByPto
        })
      });

      const data = await res.json();
      if (data.success) {
        onSuccess();
      } else {
        alert(`Ошибка при формировании АОСР: ${data.error}`);
      }
    } catch (err) {
      console.error(err);
      alert('Ошибка при отправке.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white border border-slate-200 rounded-3xl p-6 max-w-xl w-full space-y-4 shadow-2xl animate-fade-in max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-slate-100 pb-3">
          <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-emerald-600" />
            <span>Генератор Акта АОСР (РД-11-02-2006)</span>
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1.5 rounded-full hover:bg-slate-100 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-slate-600 font-semibold block mb-1">Номер Акта АОСР</label>
              <input
                type="text"
                value={actNumber}
                onChange={(e) => setActNumber(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-slate-900 font-bold focus:outline-none focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="text-slate-600 font-semibold block mb-1">Шифр проекта (РД)</label>
              <input
                type="text"
                value={projectCode}
                onChange={(e) => setProjectCode(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-slate-900 font-bold focus:outline-none focus:border-emerald-500"
              />
            </div>
          </div>

          <div>
            <label className="text-slate-600 font-semibold block mb-1">Наименование скрытых работ</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2.5 text-slate-900 font-bold focus:outline-none focus:border-emerald-500"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-slate-600 font-semibold block mb-1">Дата начала работ</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2 text-slate-900 font-medium"
              />
            </div>
            <div>
              <label className="text-slate-600 font-semibold block mb-1">Дата окончания работ</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2 text-slate-900 font-medium"
              />
            </div>
          </div>

          <div>
            <label className="text-slate-600 font-semibold block mb-1">Чертежи и рабочая документация</label>
            <input
              type="text"
              value={projectDrawings}
              onChange={(e) => setProjectDrawings(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl p-2 text-slate-900"
            />
          </div>

          {/* Certificates Selection */}
          <div className="bg-slate-50 p-3 rounded-xl border border-slate-200 space-y-2">
            <span className="text-slate-700 font-bold block">Привязка сертификатов & паспортов качества:</span>
            <div className="space-y-1.5 max-h-36 overflow-y-auto">
              {certificates.map(c => (
                <label key={c.id} className="flex items-center space-x-2 text-[11px] text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedCertIds.includes(c.id)}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedCertIds([...selectedCertIds, c.id]);
                      else setSelectedCertIds(selectedCertIds.filter(id => id !== c.id));
                    }}
                    className="w-3.5 h-3.5 text-emerald-600 rounded bg-white border-slate-300 focus:ring-0"
                  />
                  <span><strong>{c.certNumber}</strong> — {c.title} ({c.manufacturer})</span>
                </label>
              ))}
            </div>
          </div>

          <div className="pt-2 flex flex-wrap gap-2 justify-end">
            <button
              type="button"
              onClick={handlePrintPreview}
              className="flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 border border-slate-200 px-3 py-2 rounded-xl font-bold transition"
            >
              <Printer className="w-4 h-4" />
              <span>Предпросмотр</span>
            </button>
            <button
              type="button"
              onClick={handleExportDocx}
              className="flex items-center gap-1.5 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 px-3 py-2 rounded-xl font-bold transition"
            >
              <FileDown className="w-4 h-4" />
              <span>Экспорт DOCX</span>
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-4 py-2 rounded-xl shadow-md shadow-emerald-600/20 transition disabled:opacity-50"
            >
              <Send className="w-4 h-4" />
              <span>Сформировать АОСР</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
